import type { CalendarDate } from '@internationalized/date';
import {
    createProperties,
    type Field,
    Form,
    type FormState,
    type TypeToFields,
} from '@jmvdevelop/svelte-form';

type TypeOfAssuranceEnum =
    | 'auto'
    | 'home'
    | 'health'
    | 'life'
    | 'borrower'
    | 'pro'
    | 'travel'
    | 'temporary'
    | 'pet'
    | 'other';

export type AssuranceFormData = {
    assuranceType: TypeOfAssuranceEnum | null;
    assuranceTypeOther: string | null;
    isIndividualOrProfessional: string | null;
    // auto
    vehicleType: string | null;
    vehicleBrandAndModel: string | null;
    vehicleYear: number | null;
    vehicleUsage: string | null;
    desiredCoverage: string | null;
    // home
    housingType: string | null;
    isOwnerOrTenant: string | null;
    housingSurface: number | null;
    insuredGoodsValue: string | null;
    // health
    subscriberAge: number | null;
    numberOfPeopleCovered: string | null;
    desiredReimbursementLevel: string | null;
    // pro
    activitySector: string | null;
    employeeCount: string | null;
    coverageType: string | null;
    // travel
    travelDestination: string | null;
    travelDuration: string | null;
    travelNumberOfPeople: number | null;
    // pet
    animalType: string | null;
    animalBreed: string | null;
    animalAge: string | null;
    // life
    lifeMainObjective: string | null;
    lifeSubscriberAge: number | null;
    lifeEstimatedAmount: string | null;
    lifeDesignatedBeneficiary: string | null;
    // borrower
    borrowerLoanAmount: string | null;
    borrowerLoanDuration: string | null;
    borrowerPropertyTypeFinanced: string | null;
    borrowerHasKnownHealthIssues: string | null;
    borrowerWantsToDelegateInsurance: string | null;
    // temporary
    temporaryInsuredItemOrSituation: string | null;
    temporaryDesiredDuration: string | null;
    temporarySubscriptionReason: string | null;
    temporaryCoverageStartDate: CalendarDate | null;
};
type FormData = AssuranceFormData;

export type Fields<TFormValues> = TypeToFields<TFormValues, AssuranceFormData>;

export const empty: FormData = {
    assuranceType: null,
    assuranceTypeOther: null,
    isIndividualOrProfessional: null,
    vehicleType: null,
    vehicleBrandAndModel: null,
    vehicleYear: null,
    vehicleUsage: null,
    desiredCoverage: null,
    housingType: null,
    isOwnerOrTenant: null,
    housingSurface: null,
    insuredGoodsValue: null,
    subscriberAge: null,
    numberOfPeopleCovered: null,
    desiredReimbursementLevel: null,
    activitySector: null,
    employeeCount: null,
    coverageType: null,
    travelDestination: null,
    travelDuration: null,
    travelNumberOfPeople: null,
    animalType: null,
    animalBreed: null,
    animalAge: null,
    lifeMainObjective: null,
    lifeSubscriberAge: null,
    lifeEstimatedAmount: null,
    lifeDesignatedBeneficiary: null,
    borrowerLoanAmount: null,
    borrowerLoanDuration: null,
    borrowerPropertyTypeFinanced: null,
    borrowerHasKnownHealthIssues: null,
    borrowerWantsToDelegateInsurance: null,
    temporaryInsuredItemOrSituation: null,
    temporaryDesiredDuration: null,
    temporarySubscriptionReason: null,
    temporaryCoverageStartDate: null,
};

export function createFields<TValues>(self: Field<TValues, FormData>): Fields<TValues> {
    return {
        self: self,
        ...createProperties(self, [
            'assuranceType',
            'assuranceTypeOther',
            'isIndividualOrProfessional',
            'vehicleType',
            'vehicleBrandAndModel',
            'vehicleYear',
            'vehicleUsage',
            'desiredCoverage',
            'housingType',
            'isOwnerOrTenant',
            'housingSurface',
            'insuredGoodsValue',
            'subscriberAge',
            'numberOfPeopleCovered',
            'desiredReimbursementLevel',
            'activitySector',
            'employeeCount',
            'coverageType',
            'travelDestination',
            'travelDuration',
            'travelNumberOfPeople',
            'animalType',
            'animalBreed',
            'animalAge',
            'lifeMainObjective',
            'lifeSubscriberAge',
            'lifeEstimatedAmount',
            'lifeDesignatedBeneficiary',
            'borrowerLoanAmount',
            'borrowerLoanDuration',
            'borrowerPropertyTypeFinanced',
            'borrowerHasKnownHealthIssues',
            'borrowerWantsToDelegateInsurance',
            'temporaryInsuredItemOrSituation',
            'temporaryDesiredDuration',
            'temporarySubscriptionReason',
            'temporaryCoverageStartDate',
        ] as const),
    };
}

export function validateAssurance<TRootFormData>(
    _form: Form<TRootFormData, string>,
    _values: FormData,
    _fields: Fields<TRootFormData> | undefined | null,
) {
    return (state: FormState<TRootFormData, string>) => {
        return state;
    };
}
